import React, { useMemo, useCallback, useState } from 'react';
import { usePostApi } from "../../../hooks/post_api_hook/usePostApi";
import { url } from "../../../utils/confserveur";
import { GraduationCap, Users, CalendarDays, Eye, EyeOff } from 'lucide-react';
import SequencesCommunes from "../ProgressionClasse/SequencesCommunes";
import SequenceCardConsultation from "../ProgressionClasse/SequenceCardConsultation";
import ActivityListModal from "../ProgressionNiveau/ActivityListModal";
import { useClasseContext } from '../../../context/ClasseContext';

// Composant principal RoadMapGroupe
function RoadMapGroupe({
  filteredSequences,
  selectedNiveau,
  groupNom,
  selectedView,
  onChangeView,
  // Props pour SuiviProgressionGroupe
  groupes,
  listSequences,
  profGroupIds,
  onToggleAccompli,
  handleReload
}) {
  // Hook pour l'API de mise à jour
  const [{ isLoading: isUpdating }, doPost] = usePostApi(url + "sequences/add_sequence_prof/");
  const [updatingSequenceId, setUpdatingSequenceId] = useState(null);

  // State pour le modal de liste des activités
  const [isActivityListModalOpen, setIsActivityListModalOpen] = useState(false);
  const [activityListModalData, setActivityListModalData] = useState({ type: null, activities: [], sequenceTitle: '' });

  const { classeDetail } = useClasseContext();

  // Récupérer groupeId en matchant groupNom avec le tableau groupes
  const groupeId = useMemo(() => {
    if (!groupes || !groupNom) return null;
    const groupe = groupes.find(g => g.nom === groupNom);
    return groupe?.id || null;
  }, [groupes, groupNom]);

  // Filtrer uniquement les séquences (pas les activités parallèles)
  const sequences = useMemo(() => {
    if (!filteredSequences) return [];
    return filteredSequences
      .filter(seq => seq.type === 'SEQUENCE')
      .sort((a, b) => new Date(a.start) - new Date(b.start));
  }, [filteredSequences]);

  // Filtrer les groupes par le niveau actuel (pour SequencesGroupesCommune)
  const filteredGroupesByNiveau = useMemo(() => {
    if (!groupes || !selectedNiveau) return [];
    return groupes.filter((groupe) => groupe.niveau === selectedNiveau);
  }, [groupes, selectedNiveau]);

  // Récupérer les activités parallèles (automatismes, qflash, résolutions)
  // Groupées par séquence selon la correspondance des dates
  const activitesParalleles = useMemo(() => {
    if (!filteredSequences) return {};
    const activites = {};

    filteredSequences
      .filter(seq => seq.type !== 'SEQUENCE')
      .forEach(act => {
        // Trouver la séquence correspondante par date
        const matchingSeq = sequences.find(seq => {
          const actStart = new Date(act.start);
          const seqStart = new Date(seq.start);
          const seqEnd = new Date(seq.end);
          return actStart >= seqStart && actStart <= seqEnd;
        });

        if (matchingSeq) {
          if (!activites[matchingSeq.id]) {
            activites[matchingSeq.id] = [];
          }
          activites[matchingSeq.id].push(act);
        }
      });

    return activites;
  }, [filteredSequences, sequences]);

  // Fonction pour vérifier si une séquence est "vue" pour le groupe actuel
  // Simple : on regarde si groupeId est dans le tableau groupes de la séquence
  const isSequenceVue = useCallback((sequence) => {
    if (!groupeId || !sequence.groupes) return false;
    return sequence.groupes.includes(groupeId);
  }, [groupeId]);
  
  // Calculer les stats basées sur "vu/pas vu"
  const stats = useMemo(() => {
    const completed = sequences.filter(seq => isSequenceVue(seq)).length;
    const locked = sequences.filter(seq => !isSequenceVue(seq)).length;
    return { completed, locked };
  }, [sequences, isSequenceVue]);

  // Ouvrir le modal de liste des activités
  const handleViewActivities = (type, activities, sequenceTitle) => {
    setActivityListModalData({ type, activities, sequenceTitle });
    setIsActivityListModalOpen(true);
  };

  // Fermer le modal de liste des activités
  const handleCloseActivityList = () => {
    setIsActivityListModalOpen(false);
    setActivityListModalData({ type: null, activities: [], sequenceTitle: '' });
  };

  // Fonction pour basculer le statut "vu" d'une séquence
  const handleToggleVu = useCallback(async (sequenceId, newVuStatus) => {
    if (!groupeId) return;

    setUpdatingSequenceId(sequenceId);

    const progressions = {
      niveau: selectedNiveau,
      add: newVuStatus ? [{ sequenceId, groupId: groupeId }] : [],
      remove: !newVuStatus ? [{ sequenceId, groupId: groupeId }] : []
    };

    try {
      await doPost(progressions);
      handleReload && handleReload();
    } catch (error) {
      console.error("Erreur lors de la mise à jour:", error);
      alert("Erreur lors de la mise à jour du statut");
    } finally {
      setUpdatingSequenceId(null);
    }
  }, [groupeId, selectedNiveau, doPost, handleReload]);

  return (
    <div className="min-h-screen w-full bg-gradient-to-br from-sky-50 via-blue-100 to-cyan-100 text-gray-800 overflow-x-hidden">
      {/* Background liquid morphing blobs */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        {/* Liquid morphing blobs - style CardClasse */}
        <svg className="absolute top-0 left-0 w-full h-full" viewBox="0 0 400 400" preserveAspectRatio="xMidYMid slice">
          <defs>
            <linearGradient id="blobGradient1Groupe" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#a5f3fc" stopOpacity="0.6" />
              <stop offset="100%" stopColor="#67e8f9" stopOpacity="0.3" />
            </linearGradient>
            <linearGradient id="blobGradient2Groupe" x1="0%" y1="100%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#bae6fd" stopOpacity="0.5" />
              <stop offset="100%" stopColor="#7dd3fc" stopOpacity="0.2" />
            </linearGradient>
            <linearGradient id="blobGradient3Groupe" x1="100%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="#e0f2fe" stopOpacity="0.7" />
              <stop offset="100%" stopColor="#bae6fd" stopOpacity="0.4" />
            </linearGradient>
          </defs>

          {/* Blob 1 - Top left */}
          <path fill="url(#blobGradient1Groupe)">
            <animate
              attributeName="d"
              dur="12s"
              repeatCount="indefinite"
              values="
                M40,80 Q80,40 120,70 T160,90 Q140,130 100,140 T40,120 Q20,100 40,80;
                M50,70 Q90,30 130,60 T170,85 Q155,125 110,145 T50,130 Q25,105 50,70;
                M40,80 Q80,40 120,70 T160,90 Q140,130 100,140 T40,120 Q20,100 40,80
              "
            />
          </path>

          {/* Blob 2 - Top right */}
          <path fill="url(#blobGradient2Groupe)">
            <animate
              attributeName="d"
              dur="15s"
              repeatCount="indefinite"
              values="
                M280,60 Q330,40 360,80 T380,130 Q350,160 310,150 T270,110 Q260,80 280,60;
                M290,50 Q340,25 375,70 T390,125 Q365,165 320,160 T275,115 Q255,75 290,50;
                M280,60 Q330,40 360,80 T380,130 Q350,160 310,150 T270,110 Q260,80 280,60
              "
            />
          </path>

          {/* Blob 3 - Bottom center */}
          <path fill="url(#blobGradient3Groupe)">
            <animate
              attributeName="d"
              dur="18s"
              repeatCount="indefinite"
              values="
                M150,280 Q200,250 250,270 T300,310 Q280,360 220,370 T150,340 Q120,310 150,280;
                M140,290 Q195,255 255,280 T310,320 Q285,375 225,380 T145,345 Q110,315 140,290;
                M150,280 Q200,250 250,270 T300,310 Q280,360 220,370 T150,340 Q120,310 150,280
              "
            />
          </path>

          {/* Blob 4 - Left side */}
          <path fill="url(#blobGradient1Groupe)">
            <animate
              attributeName="d"
              dur="20s"
              repeatCount="indefinite"
              values="
                M20,180 Q50,150 80,170 T100,220 Q80,260 50,250 T20,210 Q10,195 20,180;
                M25,175 Q60,140 90,165 T115,215 Q90,265 55,258 T22,215 Q8,198 25,175;
                M20,180 Q50,150 80,170 T100,220 Q80,260 50,250 T20,210 Q10,195 20,180
              "
            />
          </path>

          {/* Blob 5 - Right side */}
          <path fill="url(#blobGradient2Groupe)">
            <animate
              attributeName="d"
              dur="14s"
              repeatCount="indefinite"
              values="
                M340,220 Q380,200 390,240 T370,290 Q340,310 310,290 T320,240 Q330,210 340,220;
                M345,215 Q390,190 400,235 T385,295 Q350,320 315,295 T325,235 Q335,200 345,215;
                M340,220 Q380,200 390,240 T370,290 Q340,310 310,290 T320,240 Q330,210 340,220
              "
            />
          </path>
        </svg>

        {/* Effet de lumière */}
        <div className="absolute top-20 left-1/4 w-64 h-64 bg-sky-200/30 rounded-full blur-3xl animate-float" />
        <div className="absolute bottom-40 right-1/4 w-56 h-56 bg-cyan-200/40 rounded-full blur-3xl animate-float-delayed" />

        {/* Grid pattern */}
        <div
          className="absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage: 'radial-gradient(circle at 1px 1px, #0ea5e9 1px, transparent 1px)',
            backgroundSize: '40px 40px'
          }}
        />
      </div>

      {/* Header */}
      <header className="relative z-20 pt-4 sm:pt-6 md:pt-10 pb-4 sm:pb-6 px-3 sm:px-4 md:px-8">
        <div className="max-w-6xl mx-auto">
          {/* Titre */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4 sm:mb-6">
            <div className="flex items-center gap-2 sm:gap-4">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-white/70 backdrop-blur-md rounded-lg sm:rounded-xl flex items-center justify-center border border-sky-200 shadow-lg flex-shrink-0">
                <Users className="w-5 h-5 sm:w-7 sm:h-7 text-blue-600" />
              </div>
              <div className="min-w-0">
                <h1 className="text-xl font-extrabold text-slate-800 leading-tight flex items-center gap-1 sm:gap-2 flex-wrap">
                  <span className="truncate">Programmation {groupNom}</span>
                </h1>
                <p className="text-sm text-slate-500">
                  Niveau {selectedNiveau} · Mode consultation groupe
                </p>
              </div>
            </div>

            {/* Onglets de navigation */}
            <div className="flex items-center bg-white/70 backdrop-blur-md rounded-xl sm:rounded-2xl border border-sky-200 shadow-lg p-1">
              <button
                onClick={() => onChangeView && onChangeView("planning")}
                className={`flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 sm:py-2.5 rounded-lg sm:rounded-xl font-medium text-xs sm:text-sm transition-all duration-300 ${
                  selectedView === 'planning'
                    ? 'bg-gradient-to-r from-blue-500 to-sky-500 text-white shadow-lg'
                    : 'text-gray-600 hover:bg-sky-100'
                }`}
              >
                <CalendarDays className="w-4 h-4" />
                <span className="hidden xs:inline">Planning</span>
              </button>
              <button
                onClick={() => onChangeView && onChangeView("progress")}
                className={`flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 sm:py-2.5 rounded-lg sm:rounded-xl font-medium text-xs sm:text-sm transition-all duration-300 ${
                  selectedView === 'progress'
                    ? 'bg-gradient-to-r from-blue-500 to-sky-500 text-white shadow-lg'
                    : 'text-gray-600 hover:bg-sky-100'
                }`}
              >
                <GraduationCap className="w-4 h-4" />
                <span className="hidden xs:inline">Suivi</span>
              </button>
            </div>
          </div>

          {/* Stats rapides */}
          <div className="grid grid-cols-3 gap-2 sm:gap-3 md:gap-4">
            <div className="p-2.5 sm:p-3 md:p-4 bg-white/70 backdrop-blur-md rounded-xl sm:rounded-2xl border border-sky-200 shadow-lg">
              <div className="flex items-center gap-1.5 sm:gap-2 mb-0.5 sm:mb-1">
                <Eye className="w-3 h-3 sm:w-4 sm:h-4 text-emerald-500" />
                <span className="text-[10px] sm:text-xs text-gray-600 uppercase tracking-wider">Vues</span>
              </div>
              <p className="text-xl sm:text-2xl md:text-3xl font-black text-emerald-600">{stats.completed}</p>
            </div>
            <div className="p-2.5 sm:p-3 md:p-4 bg-white/70 backdrop-blur-md rounded-xl sm:rounded-2xl border border-sky-200 shadow-lg">
              <div className="flex items-center gap-1.5 sm:gap-2 mb-0.5 sm:mb-1">
                <EyeOff className="w-3 h-3 sm:w-4 sm:h-4 text-orange-500" />
                <span className="text-[10px] sm:text-xs text-gray-600 uppercase tracking-wider">À voir</span>
              </div>
              <p className="text-xl sm:text-2xl md:text-3xl font-black text-orange-500">{stats.locked}</p>
            </div>
            <div className="p-2.5 sm:p-3 md:p-4 bg-white/70 backdrop-blur-md rounded-xl sm:rounded-2xl border border-sky-200 shadow-lg">
              <div className="flex items-center gap-1.5 sm:gap-2 mb-0.5 sm:mb-1">
                <span className="text-[10px] sm:text-xs text-gray-600 uppercase tracking-wider">📈 Total</span>
              </div>
              <p className="text-xl sm:text-2xl md:text-3xl font-black text-sky-600">{sequences.length}</p>
            </div>
          </div>
              {/* Légende activités */}
              <div className="flex flex-wrap items-center gap-2 sm:gap-4 md:gap-6 mt-3 sm:mt-4 md:mt-6 justify-center">
                <span className="text-[10px] sm:text-xs text-gray-600 uppercase tracking-wider font-semibold hidden sm:inline">Activités :</span>
                <div className="flex items-center gap-1 sm:gap-2 text-[10px] sm:text-xs text-emerald-800 font-bold bg-emerald-100 px-2 py-1 rounded-lg">
                  <span>⚡</span> <span className="hidden xs:inline">Automatismes</span><span className="xs:hidden">Auto.</span>
                </div>
                <div className="flex items-center gap-1 sm:gap-2 text-[10px] sm:text-xs text-rose-800 font-bold bg-rose-100 px-2 py-1 rounded-lg">
                  <span>🧩</span> <span className="hidden xs:inline">Résolutions</span><span className="xs:hidden">Résol.</span>
                </div>
                <div className="flex items-center gap-1 sm:gap-2 text-[10px] sm:text-xs text-amber-800 font-bold bg-amber-100 px-2 py-1 rounded-lg">
                  <span>💡</span> QFlash
                </div>
              </div>
        </div>
      </header>

      {/* Timeline - visible uniquement en mode planning */}
      {selectedView === 'planning' && (
        <main className="relative z-10 px-3 sm:px-4 md:px-8 pb-10 sm:pb-16 md:pb-20">
          <div className="max-w-6xl mx-auto">
            {/* Ligne verticale - cachée sur mobile */}
            <div className="hidden sm:block absolute left-1/2 top-0 bottom-0 w-0.5 sm:w-1 -translate-x-1/2">
              <div className="absolute inset-0 bg-gradient-to-b from-blue-400/60 via-sky-400/40 to-cyan-300/20 rounded-full" />
            </div>

            {/* Séquences */}
            {sequences.length > 0 ? (
              <div className="relative space-y-6 sm:space-y-10 md:space-y-12 py-6 sm:py-8 md:py-12">
                {sequences.map((sequence, index) => (
                  <SequenceCardConsultation
                    key={sequence.id}
                    sequence={sequence}
                    index={index}
                    isVu={isSequenceVue(sequence)}
                    onToggleVu={handleToggleVu}
                    isLoading={updatingSequenceId === sequence.id}
                    activitesParalleles={activitesParalleles[sequence.id]}
                    onViewActivities={handleViewActivities}
                    classeDetail={classeDetail}
                  />
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-12 sm:py-16 md:py-20">
                <div className="w-16 h-16 sm:w-20 sm:h-20 md:w-24 md:h-24 bg-white/70 backdrop-blur-md rounded-full flex items-center justify-center mb-4 sm:mb-6 border border-sky-200 shadow-lg">
                  <span className="text-2xl sm:text-3xl md:text-4xl">📚</span>
                </div>
                <h3 className="text-lg sm:text-xl font-bold text-gray-800 mb-2">Aucune séquence</h3>
                <p className="text-gray-600 text-center max-w-md text-sm sm:text-base px-4">
                  Il n'y a pas encore de séquence créée pour ce niveau.
                  Contactez le coordonnateur de discipline pour créer la progression commune.
                </p>
              </div>
            )}

            {/* Fin d'année marker */}
            {sequences.length > 0 && (
              <div className="relative flex justify-center mt-8 sm:mt-10 md:mt-12">
                <div className="w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 rounded-xl sm:rounded-2xl bg-gradient-to-br from-emerald-400 to-cyan-500 flex items-center justify-center shadow-2xl shadow-emerald-500/40 border-2 sm:border-4 border-white">
                  <span className="text-xl sm:text-2xl">🎓</span>
                </div>
              </div>
            )}
          </div>
        </main>
      )}

      {/* Suivi des progrès - visible uniquement en mode progress */}
      {selectedView === 'progress' && (
        <div className="px-3 sm:px-4 md:px-8 pb-10">
          <div className="max-w-6xl mx-auto">
            <SequencesCommunes
              sequenceProf={filteredSequences}
              filteredGroupes={filteredGroupesByNiveau}
              niveau={selectedNiveau}
              mode="groupe"
            />
          </div>
        </div>
      )}

      {/* Animations */}
      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0) scale(1); }
          50% { transform: translateY(-20px) scale(1.05); }
        }
        @keyframes float-delayed {
          0%, 100% { transform: translateY(0) scale(1); }
          50% { transform: translateY(20px) scale(1.02); }
        }
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-float {
          animation: float 8s ease-in-out infinite;
        }
        .animate-float-delayed {
          animation: float-delayed 10s ease-in-out infinite;
        }
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out;
        }
      `}</style>

      {/* Modal Liste des Activités (consultation seule - pas de bouton supprimer) */}
      <ActivityListModal
        isOpen={isActivityListModalOpen}
        onClose={handleCloseActivityList}
        type={activityListModalData.type}
        activities={activityListModalData.activities}
        sequenceTitle={activityListModalData.sequenceTitle}
      />
    </div>
  );
}

export default RoadMapGroupe;
